import { useMemo, useState } from 'react';

import { ORGANIZATION_DISPLAY_NAME_KEY } from '@/hooks/profilePicture';
import { useMy } from '@/hooks/useMy';
import type { ExportFormatType } from '@/hooks/useReportExport';
import { ExportFormat } from '@/hooks/useReportExport';
import { useUpdateSetting } from '@/hooks/useSetting';
import { useGetSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/state/auth';
import { omit } from '@/utils/lodash.util';

/**
 * The full set of report-config form values used by the Praetorian PDF/ZIP
 * report exporter (`GenerateReportWizard`).
 */
export interface PDFExportFormValues {
  title: string;
  clientName: string;
  target: string;
  draft: boolean;
  version: string;
  sow: string;
  footer: string;
  confidentialLabel: string;
  retest: boolean;
  startDate: string; // yyyy-MM-dd, defaults from subscription; never persisted
  endDate: string; // yyyy-MM-dd, defaults from subscription; never persisted
  executiveSummaryPath: string; // "" = don't include
  narrativesPath: string; // "" = don't include
  appendixPath: string; // "" = don't include
  exportFormat: ExportFormatType;
  groupBy: string;
}

export interface ReportFileVariants {
  executiveSummary: string[];
  narratives: string[];
  appendix: string[];
}

export interface UseReportConfigFormReturn {
  /** Merged form values: defaults ← saved settings ← user overrides. */
  current: PDFExportFormValues;
  /** Apply a single-field user override for the current session. */
  handleFieldChange: <K extends keyof PDFExportFormValues>(
    field: K,
    value: PDFExportFormValues[K]
  ) => void;
  /** Replace the whole session-override map (used to apply on-open overrides). */
  setUserOverrides: (overrides: Partial<PDFExportFormValues>) => void;
  /**
   * Persist the merged values back to the `pdf-export-options` setting.
   * Engagement dates are never persisted. When `excludeMarkdownToggles` is true
   * the legacy `includeNarratives`/`includeAppendix` keys are stripped so the
   * universal export modal (which owns those toggles separately) isn't stomped.
   */
  persistFormValues: (excludeMarkdownToggles: boolean) => void;
  /** Discovered markdown variant file lists for each report section. */
  fileVariants: ReportFileVariants;
  /** True while settings or subscription data are still loading. */
  isDataLoading: boolean;
}

/**
 * `useReportConfigForm` — the shared data + persistence layer for the Praetorian
 * report exporters.
 *
 * Owns the three-layer form state (`{ ...defaultFormValues, ...savedExportOptions,
 * ...userOverrides }`), markdown-variant discovery, and the `persistFormValues`
 * round-trip through `useUpdateSetting`. `GenerateReportWizard` consumes this so
 * the report-config fields, defaults, and persistence rules stay in lockstep.
 *
 * The component owns the *open/close transition* logic (applying
 * `initialExportFormat` / `skipMarkdownDefaults` on open, or resetting via a
 * remount key) — that is consumer-specific and stays in the component.
 */
export function useReportConfigForm(): UseReportConfigFormReturn {
  const { me, friend } = useAuth();
  const customerEmail = friend || me;

  // Fetch org settings to pre-populate client name + read saved export options.
  const { data: settings, status: settingsStatus } = useMy(
    { resource: 'setting' },
    { email: customerEmail, staleTime: 30000, gcTime: 300000 }
  );

  // Fetch available files to populate variant dropdowns.
  const { data: files = [] } = useMy(
    { resource: 'file' },
    { email: customerEmail }
  );

  const organizationName = useMemo(() => {
    const setting = settings?.find(
      s => s.name === ORGANIZATION_DISPLAY_NAME_KEY
    );
    return typeof setting?.value === 'string' ? setting.value : '';
  }, [settings]);

  // Fetch subscription to pre-populate dates and customer domain.
  const { data: subscriptionData, status: subscriptionStatus } =
    useGetSubscription({ email: customerEmail });

  // Build variant file lists for each report section.
  const fileVariants = useMemo((): ReportFileVariants => {
    const esVariants = files
      .filter(
        f =>
          f.name === 'executive_summary.md' ||
          f.name.match(/^reports\/executive_summary.*\.md$/)
      )
      .map(f => f.name);
    const narrVariants = files
      .filter(
        f =>
          f.name === 'narratives.md' ||
          f.name.match(/^reports\/narratives.*\.md$/)
      )
      .map(f => f.name);
    const appVariants = files
      .filter(
        f =>
          f.name === 'appendix.md' || f.name.match(/^reports\/appendix.*\.md$/)
      )
      .map(f => f.name);

    // Ensure root primary is first if present.
    const sortPrimary = (variants: string[], primary: string) => {
      const sorted = variants.filter(v => v !== primary);
      if (variants.includes(primary)) sorted.unshift(primary);
      return sorted;
    };

    return {
      executiveSummary: sortPrimary(esVariants, 'executive_summary.md'),
      narratives: sortPrimary(narrVariants, 'narratives.md'),
      appendix: sortPrimary(appVariants, 'appendix.md'),
    };
  }, [files]);

  // Compute default form values from fetched data.
  const defaultFormValues = useMemo((): PDFExportFormValues => {
    return {
      title: 'Penetration Test Report',
      clientName: organizationName,
      target: 'Environment',
      draft: true,
      retest: false,
      version: '',
      sow: '',
      footer: '',
      confidentialLabel: 'Confidential',
      startDate: subscriptionData?.startDate ?? '',
      endDate: subscriptionData?.endDate ?? '',
      executiveSummaryPath: 'executive_summary.md',
      narrativesPath: 'narratives.md',
      appendixPath: 'appendix.md',
      exportFormat: ExportFormat.PDF,
      groupBy: 'attack_surface',
    };
  }, [organizationName, subscriptionData]);

  // Customer-wide PDF export settings from the backend.
  const savedExportOptions = useMemo(() => {
    const setting = settings?.find(s => s.name === 'pdf-export-options');
    return setting?.value as PDFExportFormValues | undefined;
  }, [settings]);

  // User's form edits for the current session, layered on top of saved settings.
  const [userOverrides, setUserOverrides] = useState<
    Partial<PDFExportFormValues>
  >({});

  const { mutate: saveExportSettings } = useUpdateSetting(customerEmail);

  const current: PDFExportFormValues = {
    ...defaultFormValues,
    ...(savedExportOptions ?? {}),
    ...userOverrides,
  };

  const isDataLoading =
    settingsStatus === 'pending' || subscriptionStatus === 'pending';

  function handleFieldChange<K extends keyof PDFExportFormValues>(
    field: K,
    value: PDFExportFormValues[K]
  ) {
    setUserOverrides(prev => ({ ...prev, [field]: value }));
  }

  const persistFormValues = (excludeMarkdownToggles: boolean) => {
    // Dates are engagement-specific — never persist them, so each session
    // re-defaults from the subscription.
    const base = omit(current, 'startDate', 'endDate');
    const rest = omit(base, 'includeNarratives', 'includeAppendix');
    saveExportSettings({
      name: 'pdf-export-options',
      value: excludeMarkdownToggles ? rest : base,
      silent: true,
    });
  };

  return {
    current,
    handleFieldChange,
    setUserOverrides,
    persistFormValues,
    fileVariants,
    isDataLoading,
  };
}
